import { IHeaderCol } from "../Interfaces";
import { CoreFrameGrid } from "../index";
import { Constants } from "../Constants";
import { Utilities } from "../Utilities";

export class ColumnWidthHelper {

    private static canvas: any;
    

    public static checkFittedColumns(grid: CoreFrameGrid): void {
        grid.getAllVisibleLeafColumns().forEach((col: IHeaderCol, index: number) => {
            ColumnWidthHelper.checkFittedColumn(grid, index, col);
        });
    }

    public static checkFittedColumn(grid: CoreFrameGrid, colIndex: number, col: IHeaderCol): void {
        if (!col.width)
            return; 
        
        let width = ColumnWidthHelper.formatWidth(col.width);
        let titleWidth = ColumnWidthHelper.getWidthByValue(grid, col.title, null) + ColumnWidthHelper.getPaddings(grid);
        
        const allValues = grid.getBody().getAllValuesForColumn(col);
        let maxWidth = ColumnWidthHelper.calcMaxWidth(grid, col, allValues);

        ColumnWidthHelper.addOrRemoveFittedClassForHeader(grid, colIndex, col, Utilities.isLargerOrEqual(width, titleWidth));
        ColumnWidthHelper.addOrRemoveFittedClassForBody(grid, colIndex, col, Utilities.isLargerOrEqual(width, maxWidth));
    }

    private static formatWidth(width: number | string): string {
        if (typeof (width) === 'number')
            return Math.ceil(width) + 'px'; // Aufrunden, damit bei eigentlicher Breite von 101.4 Pixeln nicht zu wenig Platz verwendet wird
        else
            return width;
    }

    public static addOrRemoveFittedClass(grid: CoreFrameGrid, colIndex: number, col: IHeaderCol, add: boolean){
        ColumnWidthHelper.addOrRemoveFittedClassForHeader(grid, colIndex, col, add);
        ColumnWidthHelper.addOrRemoveFittedClassForBody(grid, colIndex, col, add);
    }

    public static addOrRemoveFittedClassForHeader(grid: CoreFrameGrid, colIndex: number, col: IHeaderCol, add: boolean){
        let headerColIndex = grid.getHeader().getHeaderRowColIndexAndRowIndex(col);
        let headerCol = grid.getHeader().$thead.find(`[data-index="${headerColIndex.row}-${headerColIndex.col}"]`);

        if (add) { 
            headerCol.addClass('fitted'); 
        } else { 
            headerCol.removeClass('fitted');
        }
    }

    public static addOrRemoveFittedClassForBody(grid: CoreFrameGrid, colIndex: number, col: IHeaderCol, add: boolean) {
        let callback;
        if (add) {
            callback = (index: number, element: HTMLElement) => {
                
                $($(element).find('td').get(colIndex)).addClass('fitted');
            }
        } else {
            callback = (index: number, element: HTMLElement) => {
                $($(element).find('td').get(colIndex)).removeClass('fitted');
            }
        }

        grid.getBody().$tbody.find('tr').each(callback);
    }

    public static autoCalcWidth(grid: CoreFrameGrid, col: IHeaderCol, values: Array<any>, average: number, fallbackTR: HTMLTableRowElement): string {
        if (col.width && col.widthAutoCalculated !== true) {
            return ColumnWidthHelper.formatWidth(col.width);
        }

        let title = col.title;
        let titleWidth = this.getWidthByValue(grid, title, fallbackTR);

        let widths = [];

        if (values != null) {
            let limit = values.length > Constants.CALC_COLUMN_LIMIT ? Constants.CALC_COLUMN_LIMIT : values.length;

            for (let i = 0; i < limit; i++) {
                let width = this.getWidthByValue(grid, values[i], fallbackTR);
                widths.push(width);
            }
        }

        let result = widths.length != 0 ? Utilities.average(widths) : average;

        result += 1; // Sicher gehen, dass minimale Abweichungen (in den Nachkommastellen) nicht zu zu wenig berechnetem Platz führt

        if (result < Constants.MIN_COLUMN_WIDTH) {
            result = Constants.MIN_COLUMN_WIDTH;
        } else if (result > Constants.MAX_COLUMN_WIDTH) {
            result = Constants.MAX_COLUMN_WIDTH;
        }

        // TitleWidth is minimum width
        if (result < titleWidth) {
            result = titleWidth
        }

        if (col.additionalWidth)
            result += col.additionalWidth;

        result += ColumnWidthHelper.getPaddings(grid);

        col.widthAutoCalculated = true;

        return ColumnWidthHelper.formatWidth(result);
    }

    public static calcMaxWidth(grid: CoreFrameGrid, col: IHeaderCol, values: Array<any>): string {
        let title = col?.title ?? "";
        const minWidth = grid.getOptions().colMinWidth ?? Constants.MIN_COLUMN_WIDTH;

        // Sotiersymbol nimmt zusätzlichen Platz ein 
        if (col != null && grid.getHeader().isSortedBy(col)) {
            title = title + "..."
        }

        let result = 0;
        let titleWidth = this.getWidthByValue(grid, title, null);

        let widths = [];
        widths.push(titleWidth)

        let limit = values.length > Constants.CALC_COLUMN_LIMIT ? Constants.CALC_COLUMN_LIMIT : values.length;

        for (let i = 0; i < limit; i++) {
            let width = this.getWidthByValue(grid, values[i], null);
            widths.push(width);
        }

        if (widths.length > 0) {
            result = Math.max.apply(Math, widths);
        }

        if (result < minWidth) {
            result = minWidth;
        }

        if (col?.additionalWidth != null)
            result += col.additionalWidth;

        result += ColumnWidthHelper.getPaddings(grid);    

        return ColumnWidthHelper.formatWidth(result);
    }

    private static getWidthByValue(grid: CoreFrameGrid, value: any, fallbackTR: HTMLTableRowElement) {
        const headRowStyle = grid.getHeader().getStylingInformation();
        let metrics = ColumnWidthHelper.calculateByFontSize(headRowStyle.fontSize, headRowStyle.fontFamily, headRowStyle.fontWeight, value);

        return metrics;
    }

    public static calculateByFontSize(fontSize: string, fontFamily: string, fontWeight: string, value: any) {
        if (!value)
            return 0;

        if (value instanceof jQuery) {
            //Verwenden von innerText statt text, da letzteres den gesamten DOM-Teilbaum inkl.
            //versteckter Kind - Elemente ließt, was die Zeichenanzahl verfälscht
            value = (value as JQuery<HTMLElement>).prop("innerText");
        } else if (value instanceof HTMLElement) {
            value = value.innerText;
        }
           
        let canvas = ColumnWidthHelper.canvas || (ColumnWidthHelper.canvas = document.createElement("canvas"));
        let context = canvas.getContext("2d");

        let bodyElement = document.getElementsByTagName('body')[0];
        let fontFamilyComputed = window.getComputedStyle(bodyElement).getPropertyValue('font-family');
        let fontSizeComputed = window.getComputedStyle(bodyElement).getPropertyValue('font-size');

        context.font = `${fontWeight} ${fontSize.length > 1 ? fontSize : fontSizeComputed} ${fontFamily.length > 1 ? fontFamily : fontFamilyComputed}`;

        let metrics = context.measureText(value);

        return metrics.width;
    }

    public static getPaddings(grid: CoreFrameGrid): number {
        const anyCell = grid.getHeader().$thead.find('th') as JQuery<HTMLTableCellElement>;

        if (anyCell.length == 0) {
            return 0;
        }

        return parseFloat(anyCell.css('paddingLeft')) + parseFloat(anyCell.css('paddingRight'));
    }
}
